import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setVersions, setSongs } from '@state/actions/songs'
import { useVersions, useSongs } from '@state/uses/songs'
import acum from '@services/acum'
import urlUtils from '@services/urlUtils'
import PaginatedTable from '@components/Table/PaginatedTable'
import {
    TableButton,
    FormSection,
    FormRow,
    ErrorText,
    SubmitButtonsRow,
    SubmitBtnContainer,
} from '@appearance/styled'
import api from '@services/api'
import { useDanceOnAddProcess } from '@state/uses/dances'
import { setDanceOnAddProcess } from '@state/actions/dances'
import TextInput from '@components/Form/TextInput'
import SubmitButton from '@components/Buttons/SubmitButton'
import i18n from '@localization/i18n'

const extractTranslatorsString = (translators) => {
    let res = ''
    for (let i = 0; i < translators.length; i++) {
        res = res + translators[i].heb_name + ', '
    }
    return res?.slice(0, -2)
}

export default ({ term }) => {
    const songs = useSongs()
    const versions = useVersions()
    const [fakeLinkSelected, setFakeLinkSelected] = useState(0) // index of tabs - initialize with first -- total: 2
    const danceOnAddProcess = useDanceOnAddProcess()
    const originalSearchTerm = danceOnAddProcess.name
    const [performerTextSearch, setPerformerTextSearch] = useState('')
    const [secondLoadTrigger, setSecondLoadTrigger] = useState(0)
    const [getTranslatedSongs, setGetTranslatedSongs] = useState(false)
    const [resetPage, setResetPage] = useState(false)
    const [musicalData, setMusicalData] = useState(versions ? songs : null)
    const [translatedData, setTranslatedData] = useState(null)
    const [filteredMusicalData, setFilteredMusicalData] = useState(null)
    const [filteredTranslatedData, setFilteredTranslatedData] = useState(null)
    const [getAdvancedSearchResults, setGetAdvancedSearchResults] = useState(
        false
    )
    const disableAkumSearch =
        !performerTextSearch || performerTextSearch.length < 3

    useEffect(() => {
        if (disableAkumSearch) {
            setGetAdvancedSearchResults(false)
            if (fakeLinkSelected === 2) {
                getTranslatedSongs
                    ? setFakeLinkSelected(1)
                    : setFakeLinkSelected(0)
            }
        }
    }, [disableAkumSearch])

    const columns = [
        {
            property: 'work_heb_name',
            label: i18n.t('akum_results_table.work_name'),
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
            isMobileTileHeader: true,
        },
        {
            property: 'id',
            label: i18n.t('akum_results_table.work_id'),
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
        },
        {
            property: 'composers',
            label: i18n.t('akum_results_table.composer'),
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
            cell: (row) => row['composers'].map((n) => n.heb_name).join(', '),
        },
        {
            property: 'lyricists',
            label: i18n.t('akum_results_table.lyricist'),
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
            cell: (row) => row['lyricists'].map((n) => n.heb_name).join(', '),
        },
        {
            property: 'performer',
            label: i18n.t('akum_results_table.performer'),
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
            hidden: fakeLinkSelected === 0 && !getAdvancedSearchResults,
            cell: (row) => row['performer']?.['heb_name'],
        },
        {
            property: 'showVersions',
            label: '',
            dataScope: 'col',
            align: 'start',
            minWidth: '105px',
            hidden: fakeLinkSelected === 1 || fakeLinkSelected === 2,
            cell: (song) => {
                return (
                    <TableButton onClick={() => searchVersions(song)}>
                        {i18n.t('akum_results_table.show_versions')}
                    </TableButton>
                )
            },
        },
        {
            property: 'chooseVersion',
            label: '',
            dataScope: 'col',
            align: 'start',
            minWidth: '80px',
            hidden: fakeLinkSelected === 0,
            cell: (ver) => {
                return (
                    <TableButton onClick={() => checkIfSongIsTaken(ver)}>
                        {i18n.t('akum_results_table.choose_version')}
                    </TableButton>
                )
            },
        },
    ]
    const history = useHistory()
    const dispatch = useDispatch()
    useEffect(() => {
        resetSongsRes()
    }, [])

    useEffect(() => {
        if (getAdvancedSearchResults) {
            if (filteredMusicalData && !getTranslatedSongs) {
                dispatch(setSongs(filteredMusicalData))
            }
            if (filteredTranslatedData && getTranslatedSongs) {
                dispatch(setSongs(filteredTranslatedData))
            }
        } else {
            if (musicalData && fakeLinkSelected === 0) {
                dispatch(setSongs(musicalData))
            }
            if (translatedData && fakeLinkSelected === 1) {
                dispatch(setSongs(translatedData))
            }
        }
    }, [
        musicalData,
        translatedData,
        fakeLinkSelected,
        filteredMusicalData,
        filteredTranslatedData,
        getTranslatedSongs,
        getAdvancedSearchResults,
    ])

    const chooseVersion = (ver) => {
        danceOnAddProcess.performer = ver.performer.heb_name
        const translatedSongName = ver.work_eng_name
            ? ` - ${ver.work_eng_name}`
            : null
        danceOnAddProcess.song = Object.assign({}, danceOnAddProcess.song, {
            name: translatedSongName
                ? ver.work_heb_name + translatedSongName
                : ver.work_heb_name,
            akum_id: ver.id,
            publication_year: ver.publication_date,
            lyricists: ver.lyricists,
            composers: ver.composers,
            translated_version_id: ver.translated_version_id?.substring(0, 6),
        })
        dispatch(setDanceOnAddProcess(danceOnAddProcess))
        dispatch(setSongs(null))
        dispatch(setVersions(null))
        history.push({
            pathname: 'dance',
        })
    }

    const doubleSearchByTerm = (page) => {
        return (dispatch) => {
            acum.searchByTerm(term, page, false).then(
                (res) => {
                    res !== musicalData && setMusicalData(res)
                },
                (error) =>
                    (!musicalData || musicalData.length > 0) &&
                    setMusicalData([])
            )
            acum.searchByTerm(term, page, true).then(
                (res) => {
                    res !== translatedData && setTranslatedData(res)
                },
                (error) =>
                    (!translatedData || translatedData.length > 0) &&
                    setTranslatedData([])
            )
        }
    }

    const doubleSearchBySongAndPerformer = (page) => {
        dispatch(setSongs(null))
        return (dispatch) => {
            acum.searchBySongAndPerformer(
                term,
                performerTextSearch,
                page,
                false
            ).then(
                (res) => {
                    res !== filteredMusicalData && setFilteredMusicalData(res)
                },
                (error) =>
                    (!filteredMusicalData || filteredMusicalData.length > 0) &&
                    setFilteredMusicalData([])
            )
            acum.searchBySongAndPerformer(
                term,
                performerTextSearch,
                page,
                true
            ).then(
                (res) => {
                    res !== filteredTranslatedData &&
                        setFilteredTranslatedData(res)
                },
                (error) =>
                    (!filteredTranslatedData ||
                        filteredTranslatedData.length > 0) &&
                    setFilteredTranslatedData([])
            )
        }
    }

    const goShowAdvancedSearchResults = (page) => {
        resetFilteredResults()
        dispatch(setSongs(null))
        dispatch(setVersions(null))
        setResetPage(true)
        setGetAdvancedSearchResults(true)
        setFakeLinkSelected(2)
        setFilteredTranslatedData(null)
        setFilteredMusicalData(null)
        if (fakeLinkSelected === 2) {
            setSecondLoadTrigger(secondLoadTrigger + 1)
        }
        doubleSearchBySongAndPerformer(page)
    }

    const checkIfSongIsTaken = (ver) => {
        api.getDanceByAkumId(ver.id).subscribe(
            (res) => {
                //setErrorModalIsOpen(true)
            },
            (error) => {
                chooseVersion(ver)
            }
        )
    }

    const searchVersions = (song) => {
        dispatch(setVersions(null))
        danceOnAddProcess.song = Object.assign({}, danceOnAddProcess.song, {
            name: song.work_heb_name,
        })
        localStorage.setItem(
            '@markidim:song_search_state',
            window.location.search
        )
        dispatch(setDanceOnAddProcess(danceOnAddProcess))
        history.push({
            pathname: 'acum_search_version',
            search: urlUtils.buildQueryParams({
                page: 1,
                id: song.full_work_id,
                originalSearchTerm,
            }),
        })
    }

    const resetSongsRes = () => {
        dispatch(setSongs(null))
        dispatch(setVersions(null))
        setMusicalData(null)
        setTranslatedData(null)
        setFilteredMusicalData(null)
        setFilteredTranslatedData(null)
        return
    }
    const resetFilteredResults = () => {
        setFilteredTranslatedData(false)
        setFilteredTranslatedData(false)
    }

    const onMusicalTabClick = () => {
        setResetPage(false)
        setFakeLinkSelected(0)
        setGetTranslatedSongs(false)
        setResetPage(true)
    }

    const onTranslatedTabClick = () => {
        setResetPage(false)
        setFakeLinkSelected(1)
        setGetTranslatedSongs(true)
        setResetPage(true)
    }

    return (
        <OutterContainer>
            <HeaderNav>
                <div>
                    <Tab
                        fakeLinkSelected={
                            fakeLinkSelected === 0 || !getTranslatedSongs
                        }
                        onClick={onMusicalTabClick}>
                        <div className="fake-link">
                            {i18n.t('akum_results_table.musical_versions')}
                        </div>
                    </Tab>
                    <Tab
                        fakeLinkSelected={
                            fakeLinkSelected === 1 || getTranslatedSongs
                        }
                        onClick={onTranslatedTabClick}>
                        <div className="fake-link">
                            {i18n.t('akum_results_table.translated_versions')}
                        </div>
                    </Tab>
                </div>
            </HeaderNav>
            <Container>
                <PaginatedTable
                    title={i18n.t('akum_results_table.songs')}
                    columns={columns}
                    useFunction={useSongs}
                    loadAction={
                        getAdvancedSearchResults
                            ? doubleSearchBySongAndPerformer //searchBySongAndPerformer
                            : doubleSearchByTerm //searchByTerm
                    }
                    loadTrigger={
                        fakeLinkSelected === 2 ? 'searchByMultiple' : null
                    }
                    secondLoadTrigger={secondLoadTrigger}
                    hidePageSize
                    hideTableHeader
                    resetPage={resetPage}
                />
            </Container>
            <FormSection>
                <FormRow>
                    <TextInput
                        text={performerTextSearch}
                        onChangeText={(performer) =>
                            setPerformerTextSearch(performer)
                        }
                        width={'100%'}
                        required={true}
                        label={i18n.t('akum_results_table.filter_by_performer')}
                    />
                    <Flex1 />
                </FormRow>
                <FormRow>
                    {disableAkumSearch && (
                        <ErrorText>
                            {i18n.t(
                                'akum_results_table.minimum_search_length_error'
                            )}
                        </ErrorText>
                    )}
                </FormRow>
                <SubmitButtonsRow>
                    <StyledSubmitBtnContainer>
                        <SubmitButton
                            onClick={goShowAdvancedSearchResults}
                            label={i18n.t(
                                'akum_results_table.search_song_and_performer'
                            )}
                            disabled={disableAkumSearch}
                        />
                    </StyledSubmitBtnContainer>
                </SubmitButtonsRow>
            </FormSection>
        </OutterContainer>
    )
}
const StyledSubmitBtnContainer = styled(SubmitBtnContainer)`
    margin-bottom: 0px;
`

const Container = styled.div``
const OutterContainer = styled.div``

const Tab = styled.li`
    list-style-type: none;
    display: inline-flex;
    flex-direction: row;
    /* width:50px; */
    margin: 10px;
    justify-content: space-between;
    color: #ffffff;
    text-align: center;
    & a,
    & .fake-link {
        color: #fff;
        color: ${(p) => (!p.fakeLinkSelected ? '#fff' : '#007bff')};
        text-decoration: none;
        text-decoration: ${(p) => (!p.fakeLinkSelected ? 'none' : 'underline')};
        cursor: pointer;
    }
    & a:hover,
    & .fake-link:hover {
        color: #007bff;
        text-decoration: none;
    }
    & a:focus,
    & .fake-link:focus {
        outline: none !important;
    }

    & button {
        background-color: transparent;
        border: none;
        cursor: pointer;
        width: 30px;
        color: #ffffff;
        font-family: 'Arial';
        display: inline-flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
    }
    & img {
        width: 15px;
        height: auto;
    }
    & span {
        margin: 0px 8px;
        font-size: 15px;
        border: 1px solid #ffffff;
        padding: 5px;
    }
`

const HeaderNav = styled.nav`
    display: flex;
    font-size: 16px;
    align-items: center;
    flex-direction: row-reverse;
    justify-content: space-between;
    justify-content: center;
    background-color: rgb(83, 71, 103);
`
const Flex1 = styled.div`
    flex: 1;
    padding: 0 20px;
`
